// Install modal for marketplace integration / registrar kinds.
//
// Triggered by clicking "Install" on any integration or registrar tile,
// or by `el.call('openInstallableModal', opts)` from any surface.
// Keeps the user on the current page (no full-page navigation) and
// walks through:
//
//   Step 1 — Scope selection (org / workspace / project). Auto-skips
//   steps when the user has a single org or the chosen org has a single
//   workspace. Project selection is always optional. Skipped entirely
//   when `defaultScope` is pre-filled by the caller.
//
//   Step 2 — Configuration form (integration-specific fields + API key).
//   Uses IntegrationScopePicker (shared) for scope and mirrors the
//   GenericIntNewForm field pattern for config fields.
//
// Phase 4 extensibility props (NEW):
//   preselectedKind   – string slug — opens modal pre-selected on this kind,
//                       skipping the browse step. Validated against live
//                       catalogue at mount; error shown when not found.
//   defaultScope      – { type: 'org'|'workspace'|'project', id: string } —
//                       pre-fills the scope picker, skipping manual selection.
//   catalogFilter     – { category?, applicable_to_types?, applicable_to_scopes? }
//                       — constrains which catalogue kinds the user can pick.
//                       If preselectedKind violates catalogFilter, error is shown.
//   onInstalled       – (instance) => void — callback fired after a successful
//                       install (optimistic). The instance is the raw API
//                       response body. Roll-back happens if the server later
//                       rejects.
//   domain            – 'integration'|'library'|'mcp'|'plugin'|'template' —
//                       used by domain-aware write target selection in
//                       `submitInstallModal`.
//
// State lives on the marketplace host (island or canvas page):
//   host.installModal: null | { item, step:'scope'|'config', draft:{},
//                               preselectedKind?, defaultScope?,
//                               catalogFilter?, onInstalled?, domain? }
//   host._installModalSaving: bool
//   host._installModalError: string | null
//
// The modal is gated by `host.installModal` (null → hidden). It uses the
// same `Modal` overlay pattern as the rest of the editor (Modal.js in
// shared/components): fixed inset, black.45 backdrop, Esc-closes.
//
// All functions are accessed via el.call('fnName', ...) per CLAUDE.md
// rule — no imports between project files.

// ── Scope wizard helpers ────────────────────────────────────────────────

// Resolve the org list. Marketplace mirrors _authClaims; governance
// exposes jwtClaims.orgs. Try both.
const _getOrgs = (host) => {
  const claims = host?._authClaims || host?.root?._authClaims || {}
  const jwtOrgs = host?.root?.jwtClaims?.orgs || []
  const metaOrg = claims.workspace_org_id
    ? [{ id: claims.workspace_org_id, name: claims.workspace_org_id }]
    : []
  return jwtOrgs.length ? jwtOrgs : metaOrg
}

// Resolve workspace list — same dual-source as IntegrationScopePicker.
const _getWorkspaces = (host) => {
  const orgId =
    host?._authClaims?.workspace_org_id ||
    host?.root?.jwtClaims?.workspace_org_id
  const list = Array.isArray(host?.root?.workspaces)
    ? host.root.workspaces
    : host?.root?.jwtClaims?.workspaces || []
  return orgId ? list.filter((w) => !w.org_id || w.org_id === orgId) : list
}

// Resolve project list — same dual-source as IntegrationScopePicker.
const _getProjects = (host) => {
  const orgId =
    host?._authClaims?.workspace_org_id ||
    host?.root?.jwtClaims?.workspace_org_id
  if (Array.isArray(host?.root?.projects) && host.root.projects.length) {
    return host.root.projects.map((p) => ({
      id: p.id || p._id || p.key,
      key: p.key || p.slug,
      name: p.name || p.displayName || p.key
    }))
  }
  if (orgId) {
    const cache = (host?.root?._scopeProjectsByOrg || {})[orgId]
    if (Array.isArray(cache)) return cache
  }
  return []
}

// ── Config field (mirrors GenericIntConfigField but reads from installModal.draft) ──

export const MarketplaceInstallConfigField = {
  flow: 'y',
  gap: 'V',

  FieldLabel: {
    tag: 'label',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    text: (el, s) => {
      const req = s.required ? ' *' : ''
      return (s.label || s.key || '') + req
    }
  },

  FieldInput: {
    tag: 'input',
    type: (el, s) => (s.key && (s.key.includes('secret') || s.key.includes('key') || s.key.includes('token'))) ? 'password' : 'text',
    width: '100%',
    padding: 'Z',
    borderRadius: 'Y',
    background: 'document',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    color: 'title',
    fontSize: 'Z2',
    placeholder: (el, s) => s.placeholder || '',
    value: (el, s) => {
      const host = el.call('marketplaceHostOf', el)
      return host?.installModal?.draft?.[s.key] || ''
    },
    onInput: (e, el, s) => {
      const host = el.call('marketplaceHostOf', el)
      if (!host?.installModal) return
      host.update?.({
        installModal: {
          ...host.installModal,
          draft: {
            ...(host.installModal.draft || {}),
            [s.key]: e.target.value
          }
        }
      })
    }
  }
}

// ── Catalog validation error (preselectedKind not found / violates catalogFilter) ──

export const MarketplaceInstallCatalogError = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  borderRadius: 'Z',
  background: 'red.1',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'red.3',
  hide: (el) => {
    const host = el.call('marketplaceHostOf', el)
    return !host?.installModal?._catalogError
  },

  ErrorIcon: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'red',
    text: '!'
  },

  ErrorText: {
    tag: 'p',
    fontSize: 'Y',
    color: 'red',
    margin: '0',
    text: (el) => {
      const host = el.call('marketplaceHostOf', el)
      return host?.installModal?._catalogError || ''
    }
  }
}

// ── Scope step ─────────────────────────────────────────────────────────

export const MarketplaceInstallScopeStep = {
  flow: 'y',
  gap: 'A',

  ScopeTitle: {
    tag: 'h4',
    text: 'Where should this integration be installed?',
    fontSize: 'Z',
    fontWeight: '600',
    color: 'title',
    margin: '0'
  },

  ScopeHint: {
    tag: 'p',
    fontSize: 'Y',
    color: 'caption',
    margin: '0',
    text: 'Choose the org, workspace, and optionally a project to scope this integration.'
  },

  // Org-missing guard: user has no org → block with message.
  NoOrgWarning: {
    flow: 'y',
    gap: 'Z',
    padding: 'A',
    borderRadius: 'Z',
    background: 'red.1',
    border: '1px solid red.3',
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      const orgs = _getOrgs(host)
      return orgs.length > 0
    },

    NoOrgText: {
      tag: 'p',
      fontSize: 'Y',
      color: 'red',
      margin: '0',
      text: 'You need an organisation before installing integrations. Create one in your account settings.'
    }
  },

  ScopeForm: {
    flow: 'y',
    gap: 'A',
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      const orgs = _getOrgs(host)
      return orgs.length === 0
    },

    // Scope mode buttons (org / workspace / project) — mirrors IntegrationScopePicker.Modes.
    ScopeModes: {
      flow: 'x',
      gap: 'Z',

      ScopeModeOrg: {
        extends: 'Button',
        text: 'Org',
        background: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'org' ? 'blue' : 'transparent'
        },
        color: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'org' ? 'white' : 'title'
        },
        borderColor: 'line',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          if (!host?.installModal) return
          host.update?.({
            installModal: {
              ...host.installModal,
              draft: { ...(host.installModal.draft || {}), scopeType: 'org', scopeId: null }
            },
            _scopeWsQuery: '',
            _scopeProjQuery: ''
          })
        }
      },

      ScopeModeWorkspace: {
        extends: 'Button',
        text: 'Workspace',
        background: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'workspace' ? 'blue' : 'transparent'
        },
        color: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'workspace' ? 'white' : 'title'
        },
        borderColor: 'line',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          if (!host?.installModal) return
          host.update?.({
            installModal: {
              ...host.installModal,
              draft: {
                ...(host.installModal.draft || {}),
                scopeType: 'workspace',
                scopeId: (() => {
                  const ws = _getWorkspaces(host)
                  return ws.length === 1 ? (ws[0].id || ws[0].workspace_id || ws[0]._id) : null
                })()
              }
            },
            _scopeWsQuery: ''
          })
        }
      },

      ScopeModeProject: {
        extends: 'Button',
        text: 'Project (optional)',
        background: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'project' ? 'blue' : 'transparent'
        },
        color: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const scopeType = host?.installModal?.draft?.scopeType || 'org'
          return scopeType === 'project' ? 'white' : 'title'
        },
        borderColor: 'line',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          if (!host?.installModal) return
          host.update?.({
            installModal: {
              ...host.installModal,
              draft: { ...(host.installModal.draft || {}), scopeType: 'project', scopeId: null }
            },
            _scopeProjQuery: ''
          })
        }
      }
    },

    // Workspace picker — visible when scopeType === 'workspace'.
    WorkspacePicker: {
      flow: 'y',
      gap: 'Z',
      hide: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return (host?.installModal?.draft?.scopeType || 'org') !== 'workspace'
      },

      WorkspacePickerLabel: {
        tag: 'label',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '600',
        text: 'Select workspace'
      },

      WorkspaceList: {
        flow: 'y',
        maxHeight: 'E',
        overflowY: 'auto',
        borderRadius: 'Y',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        background: 'document',

        childrenAs: 'state',
        childExtends: {
          tag: 'button',
          type: 'button',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 'Z',
          align: 'center left',
          width: '100%',
          padding: 'V Z1',
          border: 'none',
          background: (el, s) => s._selected ? 'blue.1' : 'transparent',
          color: (el, s) => s._selected ? 'blue' : 'title',
          cursor: 'pointer',
          fontSize: 'Z2',
          textAlign: 'left',
          ':hover': { background: 'overlayStrong' },

          WsLabel: {
            tag: 'span',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            text: (el, s) => s._label || ''
          },
          WsCheck: {
            tag: 'span',
            fontSize: 'Y1',
            color: 'blue',
            hide: (el, s) => !s._selected,
            text: '✓'
          },

          onClick: (e, el, s) => {
            const host = el.call('marketplaceHostOf', el)
            if (!host?.installModal) return
            host.update?.({
              installModal: {
                ...host.installModal,
                draft: {
                  ...(host.installModal.draft || {}),
                  scopeType: 'workspace',
                  scopeId: s._value || null
                }
              }
            })
          }
        },
        children: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const curId = host?.installModal?.draft?.scopeId || ''
          const ws = _getWorkspaces(host).map((w) => ({
            id: w.id || w.workspace_id || w._id,
            name: w.name || w.id || w.workspace_id
          }))
          if (!ws.length) return []
          return ws.map((w) => ({
            _value: w.id,
            _label: w.name,
            _selected: w.id === curId
          }))
        },

        WorkspaceEmpty: {
          tag: 'span',
          padding: 'V Z1',
          fontSize: 'Z2',
          color: 'caption',
          fontStyle: 'italic',
          hide: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return _getWorkspaces(host).length > 0
          },
          text: 'No workspaces found in this org.'
        }
      }
    },

    // Project picker — visible when scopeType === 'project'.
    ProjectPicker: {
      flow: 'y',
      gap: 'Z',
      hide: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return (host?.installModal?.draft?.scopeType || 'org') !== 'project'
      },

      ProjectPickerLabel: {
        tag: 'label',
        fontSize: 'Y2',
        color: 'caption',
        fontWeight: '600',
        text: 'Select project (optional)'
      },

      ProjectList: {
        flow: 'y',
        maxHeight: 'E',
        overflowY: 'auto',
        borderRadius: 'Y',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        background: 'document',

        childrenAs: 'state',
        childExtends: {
          tag: 'button',
          type: 'button',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 'Z',
          align: 'center left',
          width: '100%',
          padding: 'V Z1',
          border: 'none',
          background: (el, s) => s._selected ? 'blue.1' : 'transparent',
          color: (el, s) => s._selected ? 'blue' : 'title',
          cursor: 'pointer',
          fontSize: 'Z2',
          textAlign: 'left',
          ':hover': { background: 'overlayStrong' },

          ProjLabel: {
            tag: 'span',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            text: (el, s) => s._label || ''
          },
          ProjCheck: {
            tag: 'span',
            fontSize: 'Y1',
            color: 'blue',
            hide: (el, s) => !s._selected,
            text: '✓'
          },

          onClick: (e, el, s) => {
            const host = el.call('marketplaceHostOf', el)
            if (!host?.installModal) return
            const curId = host.installModal.draft?.scopeId || ''
            // Clicking the already-selected project deselects (no project).
            const nextId = s._value === curId ? null : (s._value || null)
            host.update?.({
              installModal: {
                ...host.installModal,
                draft: {
                  ...(host.installModal.draft || {}),
                  scopeType: 'project',
                  scopeId: nextId
                }
              }
            })
          }
        },
        children: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const curId = host?.installModal?.draft?.scopeId || ''
          const projects = _getProjects(host)
          if (!projects.length) return []
          return projects.map((p) => ({
            _value: p.id || p.key,
            _label: p.name || p.key || p.id,
            _selected: (p.id || p.key) === curId
          }))
        },

        ProjectEmpty: {
          tag: 'span',
          padding: 'V Z1',
          fontSize: 'Z2',
          color: 'caption',
          fontStyle: 'italic',
          hide: (el) => {
            const host = el.call('marketplaceHostOf', el)
            return _getProjects(host).length > 0
          },
          text: 'No projects found. You can install at workspace level.'
        }
      }
    }
  }
}

// ── Config step ─────────────────────────────────────────────────────────

export const MarketplaceInstallConfigStep = {
  flow: 'y',
  gap: 'A',

  ConfigTitle: {
    tag: 'h4',
    text: (el) => {
      const host = el.call('marketplaceHostOf', el)
      const item = host?.installModal?.item
      return `Configure ${item?.name || 'integration'}`
    },
    fontSize: 'Z',
    fontWeight: '600',
    color: 'title',
    margin: '0'
  },

  // Display name field — only shown when allows_multiple.
  DisplayNameField: {
    flow: 'y',
    gap: 'V',
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      const item = host?.installModal?.item
      return !item?._liveKind?.allows_multiple
    },

    DisplayNameLabel: {
      tag: 'label',
      fontSize: 'Y2',
      color: 'caption',
      fontWeight: '700',
      text: 'Display name'
    },
    DisplayNameInput: {
      tag: 'input',
      type: 'text',
      width: '100%',
      padding: 'Z',
      borderRadius: 'Y',
      background: 'document',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return host?.installModal?.item?._liveKind?.label || 'My integration'
      },
      value: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return host?.installModal?.draft?.displayName || ''
      },
      onInput: (e, el) => {
        const host = el.call('marketplaceHostOf', el)
        if (!host?.installModal) return
        host.update?.({
          installModal: {
            ...host.installModal,
            draft: { ...(host.installModal.draft || {}), displayName: e.target.value }
          }
        })
      }
    }
  },

  // Dynamic config fields from config_schema.fields.
  DynamicConfigFields: {
    flow: 'y',
    gap: 'A',
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      const item = host?.installModal?.item
      const fields = (item?._liveKind?.config_schema?.fields || []).filter((f) => f.key !== 'display_name')
      return fields.length === 0
    },
    childrenAs: 'state',
    childExtends: 'MarketplaceInstallConfigField',
    children: (el) => {
      const host = el.call('marketplaceHostOf', el)
      const item = host?.installModal?.item
      return (item?._liveKind?.config_schema?.fields || []).filter((f) => f.key !== 'display_name')
    }
  },

  // API key / secret field — hidden for oauth2 / webhook / none auth models.
  SecretField: {
    flow: 'y',
    gap: 'V',
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      const item = host?.installModal?.item
      const kr = item?._liveKind
      if (!kr || kr.auth_model === 'none') return true
      const flow = kr.ui_flow || 'credential'
      return flow === 'oauth2' || flow === 'webhook'
    },

    SecretLabel: {
      tag: 'label',
      fontSize: 'Y2',
      color: 'caption',
      fontWeight: '700',
      text: (el) => {
        const host = el.call('marketplaceHostOf', el)
        const item = host?.installModal?.item
        return item?._liveKind?.secret_label || 'API key / Secret'
      }
    },
    SecretHint: {
      tag: 'p',
      fontSize: 'Y1',
      color: 'caption',
      margin: '0 0 V 0',
      hide: (el) => {
        const host = el.call('marketplaceHostOf', el)
        const item = host?.installModal?.item
        return !item?._liveKind?.secret_hint
      },
      text: (el) => {
        const host = el.call('marketplaceHostOf', el)
        const item = host?.installModal?.item
        return item?._liveKind?.secret_hint || ''
      }
    },
    SecretInput: {
      tag: 'input',
      type: 'password',
      width: '100%',
      padding: 'Z',
      borderRadius: 'Y',
      background: 'document',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'line',
      color: 'title',
      fontSize: 'Z2',
      placeholder: '••••••••••••••••',
      value: (el) => {
        const host = el.call('marketplaceHostOf', el)
        return host?.installModal?.draft?.secret || ''
      },
      onInput: (e, el) => {
        const host = el.call('marketplaceHostOf', el)
        if (!host?.installModal) return
        host.update?.({
          installModal: {
            ...host.installModal,
            draft: { ...(host.installModal.draft || {}), secret: e.target.value }
          }
        })
      }
    }
  },

  // No-config notice for integrations that need no configuration.
  NoConfigNotice: {
    tag: 'p',
    fontSize: 'Y',
    color: 'caption',
    margin: '0',
    fontStyle: 'italic',
    hide: (el) => {
      const host = el.call('marketplaceHostOf', el)
      const item = host?.installModal?.item
      const kr = item?._liveKind
      if (!kr) return false
      const fields = (kr.config_schema?.fields || []).filter((f) => f.key !== 'display_name')
      const authModel = kr.auth_model || 'none'
      const flow = kr.ui_flow || 'credential'
      const needsSecret = authModel !== 'none' && flow !== 'oauth2' && flow !== 'webhook'
      return fields.length > 0 || needsSecret
    },
    text: 'No configuration required. Click Install to complete the setup.'
  }
}

// ── Main modal ──────────────────────────────────────────────────────────

export const MarketplaceInstallModal = {
  // Visibility gated on host.installModal — follows the Modal.js pattern
  // (stateKey drives display) but reads from the marketplace host state
  // directly since the marketplace host may be a sub-state (island),
  // not root.
  display: (el) => {
    const host = el.call('marketplaceHostOf', el)
    return host?.installModal ? 'flex' : 'none'
  },
  position: 'fixed',
  inset: '0',
  zIndex: '2000',
  align: 'center center',
  background: 'black.45',
  backdropFilter: 'blur(4px)',
  padding: 'A',

  // Esc closes the modal via the global keydown listener registered
  // in registerEscHandler. For additional safety we also handle it here
  // in case the listener context doesn't reach.
  onKeydown: (e, el) => {
    if (e.key !== 'Escape') return
    const host = el.call('marketplaceHostOf', el)
    el.call('closeInstallModal', host)
    e.preventDefault()
  },

  // Click on backdrop (not window) closes without saving.
  onClick: (e, el) => {
    if (e.target !== el.node) return
    const host = el.call('marketplaceHostOf', el)
    el.call('closeInstallModal', host)
  },

  ModalWindow: {
    theme: 'modal',
    flow: 'y',
    gap: 'A',
    padding: 'B',
    borderRadius: 'A',
    minWidth: 'G',
    maxWidth: 'J',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: 'dialog',
    boxShadow: '0 8px 48px black.3',
    // Tab-trapping for a11y — focus stays inside the modal.
    tabindex: '-1',

    // Header: integration logo + name + close button.
    ModalTop: {
      flow: 'x',
      align: 'center space-between',
      gap: 'A',

      ModalTitleRow: {
        flow: 'x',
        align: 'center',
        gap: 'Z',

        ModalIcon: {
          align: 'center center',
          width: 'C',
          height: 'C',
          minWidth: 'C',
          borderRadius: 'Y',
          overflow: 'hidden',
          fontWeight: '700',
          fontSize: 'B',
          background: (el) => {
            const host = el.call('marketplaceHostOf', el)
            const item = host?.installModal?.item
            return item?.logo ? 'white' : el.call('getKindAccent', item?.kind, 'bg')
          },
          color: (el) => {
            const host = el.call('marketplaceHostOf', el)
            const item = host?.installModal?.item
            return el.call('getKindAccent', item?.kind, 'fg')
          },

          ModalIconImg: {
            hide: (el) => {
              const host = el.call('marketplaceHostOf', el)
              const item = host?.installModal?.item
              return !item?.logo || el.parent._imgFailed
            },
            tag: 'img',
            src: (el) => {
              const host = el.call('marketplaceHostOf', el)
              const slug = host?.installModal?.item?.logo
              return slug ? `https://cdn.simpleicons.org/${slug}` : ''
            },
            alt: (el) => {
              const host = el.call('marketplaceHostOf', el)
              return host?.installModal?.item?.name || ''
            },
            width: '60%',
            height: '60%',
            objectFit: 'contain',
            onRender: (el) => {
              if (!el.node) return
              el.node.onerror = () => {
                el.parent._imgFailed = true
                el.node.style.display = 'none'
                const letter = el.parent.ModalIconLetter
                if (letter?.node) letter.node.style.display = ''
              }
            }
          },
          ModalIconLetter: {
            hide: (el) => {
              const host = el.call('marketplaceHostOf', el)
              const item = host?.installModal?.item
              return !!item?.logo && !el.parent._imgFailed
            },
            tag: 'span',
            text: (el) => {
              const host = el.call('marketplaceHostOf', el)
              const name = host?.installModal?.item?.name || ''
              return name[0]?.toUpperCase() || '?'
            }
          }
        },

        ModalTitle: {
          flow: 'y',
          gap: 'V',

          ModalName: {
            tag: 'h3',
            text: (el) => {
              const host = el.call('marketplaceHostOf', el)
              return `Install ${host?.installModal?.item?.name || ''}`
            },
            fontSize: 'B',
            fontWeight: '700',
            color: 'title',
            margin: '0'
          },

          ModalStepLabel: {
            tag: 'span',
            fontSize: 'Y',
            color: 'caption',
            text: (el) => {
              const host = el.call('marketplaceHostOf', el)
              const modal = host?.installModal
              const step = modal?.step || 'scope'
              // When defaultScope is pre-filled, we skip scope step — show single-step label
              if (modal?.defaultScope && step === 'config') return 'Configure'
              if (modal?._catalogError) return 'Catalog error'
              return step === 'scope' ? 'Step 1 of 2: Select scope' : 'Step 2 of 2: Configure'
            }
          }
        }
      },

      ModalCloseBtn: {
        extends: 'Button',
        text: '✕',
        ariaLabel: 'Close install modal',
        flexShrink: '0',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          el.call('closeInstallModal', host)
        }
      }
    },

    // Divider.
    ModalDivider: {
      borderTop: '1px solid line',
      margin: '0 -B'
    },

    // Step body — swaps between scope and config.
    ModalBody: {
      flow: 'y',
      gap: 'A',
      flex: '1',

      // Catalog validation error — shown when preselectedKind doesn't exist
      // in catalogue or violates catalogFilter. Replaces both steps.
      MarketplaceInstallCatalogError: {},

      // Step 1: scope selection. Hidden when defaultScope is pre-filled
      // (openInstallableModal pre-validates the scope) or catalog error exists.
      MarketplaceInstallScopeStep: {
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          if (modal.defaultScope) return true  // scope step skipped when pre-filled
          return modal.step !== 'scope'
        }
      },

      // Step 2: configuration form.
      MarketplaceInstallConfigStep: {
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          // When defaultScope pre-fills scope, skip directly to config
          if (modal.defaultScope) return modal.step !== 'config'
          return modal.step !== 'config'
        }
      },

      // Error display — shown on any step when an error exists.
      ModalError: {
        tag: 'p',
        padding: 'Y A',
        borderRadius: 'Y',
        background: 'red.1',
        border: '1px solid red.3',
        fontSize: 'Y',
        color: 'red',
        margin: '0',
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return !host?._installModalError
        },
        text: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return host?._installModalError || ''
        }
      }
    },

    // Footer with navigation / action buttons.
    ModalFooter: {
      flow: 'x',
      align: 'center flex-end',
      gap: 'Z',
      borderTop: '1px solid line',
      margin: '0 -B -B',
      padding: 'A B',

      // Cancel button — always visible.
      CancelBtn: {
        extends: 'Button',
        text: 'Cancel',
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          el.call('closeInstallModal', host)
        }
      },

      // Back button — config step only; hidden when defaultScope pre-fills
      // scope (nowhere to go back to) or when catalog error is shown.
      BackBtn: {
        extends: 'Button',
        text: '← Back',
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          if (modal.defaultScope) return true  // no scope step to go back to
          return modal.step !== 'config'
        },
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          if (!host?.installModal) return
          host.update?.({
            installModal: { ...host.installModal, step: 'scope' },
            _installModalError: null
          })
        }
      },

      // Next button — scope step → config step. Hidden when defaultScope
      // is pre-filled (modal opens directly on config step) or catalog error shown.
      NextBtn: {
        extends: 'Button',
        theme: 'primary',
        text: 'Next: Configure →',
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          if (modal.defaultScope) return true  // scope step is skipped entirely
          if (modal.step !== 'scope') return true
          // Hide if org list is empty (blocked).
          const orgs = _getOrgs(host)
          return orgs.length === 0
        },
        onClick: (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          if (!host?.installModal) return
          host.update?.({
            installModal: { ...host.installModal, step: 'config' },
            _installModalError: null
          })
        }
      },

      // Install button — config step only; submits the form.
      // When defaultScope is pre-filled we open directly to config, so
      // this button is visible from the moment the modal opens.
      InstallBtn: {
        extends: 'Button',
        theme: 'primary',
        text: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return host?._installModalSaving ? 'Installing…' : 'Install'
        },
        disabled: (el) => {
          const host = el.call('marketplaceHostOf', el)
          return !!host?._installModalSaving
        },
        hide: (el) => {
          const host = el.call('marketplaceHostOf', el)
          const modal = host?.installModal
          if (!modal || modal._catalogError) return true
          if (modal.defaultScope) return modal.step !== 'config'
          return modal.step !== 'config'
        },
        onClick: async (e, el) => {
          const host = el.call('marketplaceHostOf', el)
          await el.call('submitInstallModal', host, el)
        }
      }
    }
  }
}
